import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import { validateField, validateOptionalField, DESCRIPTION_MAX_LENGTH, validateRequiredResourceTags, collectTagData } from '../../utils/formValidation'
import ResourceTagsSection from '../shared/ResourceTagsSection'
import ResourceCheckboxes from '../shared/ResourceCheckboxes'
import TCAIREMFieldsSection from '../shared/TCAIREMFieldsSection'
import CoreResourceFields from '../shared/CoreResourceFields'
import ResourceDescriptionField from '../shared/ResourceDescriptionField'
import { RequestFormStyle } from '../styles'
import { setErrorNotification } from '../../reducers/notificationReducer'
import { createVideo } from '../../reducers/videosReducer'
import useResourceSubmit from '../../hooks/useResourceSubmit'
import useSubResourceRows from '../../hooks/useSubResourceRows'


const VideoForm = () => {

    const dispatch = useDispatch()
    const { isSubmitting, submit } = useResourceSubmit()

    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(false)
    const [nameHelper, setNameHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [authors, setAuthors] = useState('')
    const [authorsError, setAuthorsError] = useState(false)
    const [authorsHelper, setAuthorsHelper] = useState('')
    const [link, setLink] = useState('')
    const [linkError, setLinkError] = useState(false)
    const [linkHelper, setLinkHelper] = useState('')
    const [date, setDate] = useState('')
    const [dateError, setDateError] = useState(false)
    const [dateHelper, setDateHelper] = useState('')
    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])
    const [adminCategoryTags, setAdminCategoryTags] = useState([])
    const [experienceTags, setExperienceTags] = useState([])
    const [demographicTags, setDemographicTags] = useState([])
    const [showTagErrors, setShowTagErrors] = useState(false)
    const [isSeminalWork, setIsSeminalWork] = useState(false)
    const [inQuiz, setInQuiz] = useState(false)
    const [isTcairem, setIsTcairem] = useState(false)
    const [isPaid, setIsPaid] = useState(false)
    const [tcairemTags, setTcairemTags] = useState([])
    const [personaTags, setPersonaTags] = useState([])

    const { rows: videos, setRows: setVideos, handleFormChange, addFields, removeFields } = useSubResourceRows()


    const reset = () => {
        setName('')
        setNameError(false)
        setNameHelper('')
        setDescription('')
        setDescriptionError(false)
        setDescriptionHelper('')
        setAuthors('')
        setAuthorsError(false)
        setAuthorsHelper('')
        setLink('')
        setLinkError(false)
        setLinkHelper('')
        setDate('')
        setDateError(false)
        setDateHelper('')
        setContentTags([])
        setMedicalTags([])
        setAdminCategoryTags([])
        setExperienceTags([])
        setDemographicTags([])
        setShowTagErrors(false)
        setIsSeminalWork(false)
        setInQuiz(false)
        setIsTcairem(false)
        setIsPaid(false)
        setTcairemTags([])
        setPersonaTags([])
        setVideos([])
    }


    const handleSubmit = (event) => {
        event.preventDefault()
        setShowTagErrors(true)
        if (
            validateField(name, setNameError, setNameHelper, 100, 'Title') &&
            validateField(authors, setAuthorsError, setAuthorsHelper, 100, 'Authors') &&
            validateField(link, setLinkError, setLinkHelper) &&
            validateField(date, setDateError, setDateHelper) &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, DESCRIPTION_MAX_LENGTH, 'Description') &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        ) {

            const data = {
                title: name,
                description: description,
                authors: authors,
                link: link,
                pub_date: date,
                is_seminal_work: isSeminalWork,
                in_quiz: inQuiz,
                is_tcairem: isTcairem,
                is_paid: isPaid,
                ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
            }

            submit(createVideo(data, videos), reset)
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    return (
        <Container {...RequestFormStyle.container}>
            <Typography {...RequestFormStyle.title.props} gutterBottom sx={RequestFormStyle.title.style}>
                Video Form
            </Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <CoreResourceFields
                        name={name} setName={setName} nameError={nameError} setNameError={setNameError} nameHelper={nameHelper} setNameHelper={setNameHelper}
                        authors={authors} setAuthors={setAuthors} authorsError={authorsError} setAuthorsError={setAuthorsError} authorsHelper={authorsHelper} setAuthorsHelper={setAuthorsHelper}
                        link={link} setLink={setLink} linkError={linkError} setLinkError={setLinkError} linkHelper={linkHelper} setLinkHelper={setLinkHelper}
                        date={date} setDate={setDate} dateError={dateError} setDateError={setDateError} dateHelper={dateHelper} setDateHelper={setDateHelper}
                        validate={validateField}
                        authorsLabel="Channel"
                        showCharCount={true}
                        nameMaxLength={100}
                        authorsMaxLength={100}
                    />
                    <ResourceDescriptionField
                        value={description} setValue={setDescription}
                        error={descriptionError} setError={setDescriptionError}
                        helper={descriptionHelper} setHelper={setDescriptionHelper}
                    />
                    <ResourceTagsSection
                        contentTags={contentTags} setContentTags={setContentTags}
                        medicalTags={medicalTags} setMedicalTags={setMedicalTags}
                        adminCategoryTags={adminCategoryTags} setAdminCategoryTags={setAdminCategoryTags}
                        experienceTags={experienceTags} setExperienceTags={setExperienceTags}
                        demographicTags={demographicTags} setDemographicTags={setDemographicTags}
                        required={true}
                        forceShowRequiredErrors={showTagErrors}
                    />
                    <ResourceCheckboxes inQuiz={inQuiz} setInQuiz={setInQuiz} isSeminalWork={isSeminalWork} setIsSeminalWork={setIsSeminalWork} />
                    <TCAIREMFieldsSection
                        isTcairem={isTcairem} setIsTcairem={setIsTcairem}
                        isPaid={isPaid} setIsPaid={setIsPaid}
                        tcairemTags={tcairemTags} setTcairemTags={setTcairemTags}
                        personaTags={personaTags} setPersonaTags={setPersonaTags}
                        forceShowRequiredErrors={showTagErrors}
                    />
                    <Grid item xs={7} sm={9}>
                        <Typography variant='h4' gutterBottom>
                            Playlist
                        </Typography>
                    </Grid>
                    <Grid item xs={5} sm={3}>
                        <Button variant='contained' sx={{width: '100%'}} onClick={()=>addFields()}>
                            Add Video&nbsp;&nbsp;&nbsp;<AddCircleIcon />
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant='p' gutterBottom>
                            You can add links to each individual video here if this is a playlist.
                            Otherwise you can leave this empty.
                        </Typography>
                    </Grid>
                    {videos.map((video, index) => (
                        <Grid item key={index} xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={9}>
                                    <Typography variant='h5' gutterBottom>
                                        Video {index+1}
                                    </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Button sx={{backgroundColor: '#D22742', width: '100%', '&:hover': {backgroundColor: '#851829'}}} variant='contained' onClick={()=>removeFields(index)}>
                                        Remove Video&nbsp;&nbsp;&nbsp;<DeleteIcon />
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name='title'
                                        required
                                        value={video.title}
                                        onChange={(event)=>handleFormChange(index, event)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Authors"
                                        name='authors'
                                        required
                                        value={video.authors}
                                        onChange={(event)=>handleFormChange(index, event)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Link"
                                        name='link'
                                        required
                                        type='url'
                                        value={video.link}
                                        onChange={(event)=>handleFormChange(index, event)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Publish Date"
                                        name='pub_date'
                                        required
                                        type='date'
                                        InputLabelProps={{ shrink: true }}
                                        value={video.pub_date}
                                        onChange={(event)=>handleFormChange(index, event)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    ))}
                    <Grid item xs={4}></Grid>
                    <Grid item xs={4}>
                        <Button sx={RequestFormStyle.button.style} {...RequestFormStyle.button.props} type='submit' disabled={isSubmitting}>Create Video</Button>
                    </Grid>
                </Grid>
            </form>
        </Container>
    )
}

export default VideoForm
